import { ButtonBase, SvgIcon, useTheme, Avatar } from "@mui/material"
import { ReactElement } from "react"

import AppTooltip from "../helper/appTooltip"

interface AvatarIconButtonProps {
    onClick: () => void
    Icon: typeof SvgIcon
    color?: "primary" | "secondary"
    tooltip?: string
}

const defaultProps = {
    color: "primary",
    tooltip: "",
}

/**
 * Reusable Icon Button for use across the application.
 *
 * @example
 * <AvatarIconButton onClick={onClick} Icon={MyIcon} color="secondary" />
 */
const AvatarIconButton = ({
    onClick,
    Icon,
    color = "primary",
    tooltip = "",
}: AvatarIconButtonProps): ReactElement => {
    const theme = useTheme()
    const isDarkMode = theme.palette.mode === "dark"
    return (
        <AppTooltip title={tooltip}>
            <ButtonBase
                sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    width: "42px",
                    height: "42px",
                }}
                onClick={onClick}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        borderRadius: "14px",
                        fontSize: "1.5rem",
                        transition: "all .2s ease-in-out",
                        background: isDarkMode
                            ? theme.palette.background.contentBackground
                            : theme.palette[color].light,
                        color: theme.palette[color].dark,
                        "&:hover": {
                            background: theme.palette[color].dark,
                            color: theme.palette[color].light,
                        },
                    }}
                    color="inherit"
                >
                    <Icon fontSize="medium" />
                </Avatar>
            </ButtonBase>
        </AppTooltip>
    )
}

AvatarIconButton.defaultProps = defaultProps

export default AvatarIconButton
